/**
 * balancePanel.ts — Full-canvas BALANCING overlay
 *
 * Rendered on top of the pause menu when the player clicks BALANCING.
 * Three tabs: COMBAT | ITEMS | HISTORY
 *
 * Usage:
 *   import { renderBalancePanel, handleBalancePanelClick, openBalancePanel } from './balancePanel';
 */

import type { GameState } from './types';
import { computeBalanceReport, type BalanceReport, type RunSnapshot } from './balanceAnalyzer';
import { getPercSlotDisplayData, getItemState } from './itemsAdapter';
import { getWeaponDef } from './weapons';
import { getWeaponSystemState } from './weaponAdapter';

// ─── State ────────────────────────────────────────────────────────────────────
let visible = false;
let activeTab: 'COMBAT' | 'ITEMS' | 'HISTORY' = 'COMBAT';
let cachedReport: BalanceReport | null = null;

// Run history — persists across runs (module-level, up to 10 snapshots)
const runHistory: RunSnapshot[] = [];
let runCounter = 0;

export function openBalancePanel() { visible = true; }
export function closeBalancePanel() { visible = false; cachedReport = null; }
export function isBalancePanelVisible() { return visible; }

/** Call when a run ends to save a snapshot */
export function captureRunSnapshot(state: GameState) {
  const report = computeBalanceReport(state);
  const ws = getWeaponSystemState();
  const snap: RunSnapshot = {
    runNum: ++runCounter,
    timestamp: Date.now(),
    timeSurvived: Math.round(state.time),
    floor: state.wave ?? 1,
    killCount: state.killCount ?? 0,
    finalLevel: state.player.level ?? 1,
    percs: [],    // populated below
    weapons: ws?.weapons?.map(w => w.definitionId) ?? [],
    items: [],
    balanceScore: report.balanceScore,
    avgPlayerTTK: report.avgPlayerTTK,
    avgEnemyTTK: report.avgEnemyTTK,
  };
  runHistory.unshift(snap);
  if (runHistory.length > 8) runHistory.pop();
}

// ─── Colours / Layout ─────────────────────────────────────────────────────────
const BG         = '#07120a';
const PANEL_BG   = 'rgba(0,0,0,0.80)';
const TEAL       = '#00ffcc';
const DIM        = 'rgba(255,255,255,0.40)';
const WHITE      = '#ffffff';
const TABS       = ['COMBAT', 'ITEMS', 'HISTORY'] as const;

function fmt1(n: number) { return n.toFixed(1); }
function fmtTime(s: number) {
  const m = Math.floor(s / 60), sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

// ─── Hit Testing ─────────────────────────────────────────────────────────────

function tabRects(w: number) {
  const TAB_W = 140, TAB_H = 36, TAB_Y = 56, GAP = 8;
  const totalW = TABS.length * TAB_W + (TABS.length - 1) * GAP;
  const startX = w / 2 - totalW / 2;
  return TABS.map((_, i) => ({ x: startX + i * (TAB_W + GAP), y: TAB_Y, w: TAB_W, h: TAB_H }));
}

function closeRect(w: number) {
  return { x: w - 60, y: 12, w: 48, h: 32 };
}

const BOARD_W = 800;
const BOARD_H = 500;

export function handleBalancePanelClick(x: number, y: number, w: number, h: number): boolean {
  if (!visible) return false;

  // Convert raw canvas coords to chalkboard-relative
  const boardX = w / 2 - BOARD_W / 2;
  const boardY = h / 2 - BOARD_H / 2;
  const bx = x - boardX;
  const by = y - boardY;

  const cr = closeRect(BOARD_W);
  if (bx >= cr.x && bx <= cr.x + cr.w && by >= cr.y && by <= cr.y + cr.h) {
    closeBalancePanel();
    return true;
  }

  const tabs = tabRects(BOARD_W);
  for (let i = 0; i < TABS.length; i++) {
    const r = tabs[i];
    if (bx >= r.x && bx <= r.x + r.w && by >= r.y && by <= r.y + r.h) {
      activeTab = TABS[i];
      cachedReport = null;
      return true;
    }
  }

  return true; // swallow all clicks while panel is open
}

export function handleBalancePanelKey(key: string): boolean {
  if (!visible) return false;
  if (key === 'escape') { closeBalancePanel(); return true; }
  return true;
}

// ─── Main Render ──────────────────────────────────────────────────────────────

export function renderBalancePanel(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  w: number,
  h: number,
) {
  if (!visible) return;

  // Refresh report at most once per open (recalc on tab switch)
  if (!cachedReport) cachedReport = computeBalanceReport(state);
  const report = cachedReport;

  // Chalkboard bounds — must match pauseMenu.ts
  const boardX = w / 2 - BOARD_W / 2;
  const boardY = h / 2 - BOARD_H / 2;

  ctx.save();

  // Clip everything inside the chalkboard rect
  ctx.beginPath();
  ctx.rect(boardX, boardY, BOARD_W, BOARD_H);
  ctx.clip();

  // Translate so (0,0) = top-left of chalkboard
  ctx.translate(boardX, boardY);

  // Background fills chalkboard only
  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, BOARD_W, BOARD_H);

  // Header bar
  ctx.fillStyle = 'rgba(0,255,200,0.07)';
  ctx.fillRect(0, 0, BOARD_W, 50);
  ctx.strokeStyle = TEAL;
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(0, 50); ctx.lineTo(BOARD_W, 50); ctx.stroke();

  ctx.fillStyle = TEAL;
  ctx.font = 'bold 18px "Orbitron", "Share Tech Mono", monospace';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText('⚖  BALANCE ANALYSIS', 24, 25);

  // Run time sub-label
  ctx.fillStyle = DIM;
  ctx.font = '11px "Share Tech Mono", monospace';
  ctx.fillText(`Floor ${state.wave ?? 1}  ·  ${fmtTime(state.time)} elapsed  ·  ${Math.round(report.killsPerMinute)} kills/min`, 24, 40);

  // Close button
  const cr = closeRect(BOARD_W);
  ctx.fillStyle = 'rgba(255,255,255,0.08)';
  ctx.fillRect(cr.x, cr.y, cr.w, cr.h);
  ctx.strokeStyle = 'rgba(255,255,255,0.3)';
  ctx.lineWidth = 1;
  ctx.strokeRect(cr.x, cr.y, cr.w, cr.h);
  ctx.fillStyle = WHITE;
  ctx.font = 'bold 13px "Share Tech Mono", monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('✕ BACK', cr.x + cr.w / 2, cr.y + cr.h / 2);

  // Tabs
  const tabs = tabRects(BOARD_W);
  for (let i = 0; i < TABS.length; i++) {
    const r = tabs[i];
    const isActive = TABS[i] === activeTab;
    ctx.fillStyle = isActive ? 'rgba(0,255,200,0.15)' : 'rgba(255,255,255,0.04)';
    ctx.fillRect(r.x, r.y, r.w, r.h);
    ctx.strokeStyle = isActive ? TEAL : 'rgba(255,255,255,0.2)';
    ctx.lineWidth = isActive ? 2 : 1;
    ctx.strokeRect(r.x, r.y, r.w, r.h);
    ctx.fillStyle = isActive ? TEAL : DIM;
    ctx.font = `${isActive ? 'bold' : 'normal'} 12px "Share Tech Mono", monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(TABS[i], r.x + r.w / 2, r.y + r.h / 2);
  }

  // Content area
  const contentY = 104;
  const contentH = BOARD_H - contentY - 16;

  if (activeTab === 'COMBAT') renderCombatTab(ctx, report, BOARD_W, contentY, contentH);
  else if (activeTab === 'ITEMS') renderItemsTab(ctx, state, BOARD_W, contentY, contentH);
  else renderHistoryTab(ctx, BOARD_W, contentY, contentH);

  ctx.restore();
}

// ─── COMBAT Tab ───────────────────────────────────────────────────────────────

function renderCombatTab(
  ctx: CanvasRenderingContext2D,
  report: BalanceReport,
  w: number,
  startY: number,
  _h: number,
) {
  const col1X = 24;
  const col2X = w / 2 + 16;
  const colW  = w / 2 - 40;
  let y1 = startY + 8;
  let y2 = startY + 8;

  // ── Balance Score gauge ───────────────────────────────────────────
  const gaugeY = startY + 8;
  const gaugeX = col1X;
  const gaugeW = w - col1X * 2;
  const gaugeH = 44;

  // Background
  ctx.fillStyle = 'rgba(0,0,0,0.5)';
  roundRect(ctx, gaugeX, gaugeY, gaugeW, gaugeH, 6); ctx.fill();

  // Score fill — colour shifts red → yellow → green
  const score = report.balanceScore / 100;
  const scoreColor = score < 0.35
    ? '#ff3333'
    : score < 0.65
      ? '#ffcc00'
      : score < 0.85
        ? '#00ff88'
        : '#ff44cc'; // god mode = pink
  ctx.fillStyle = scoreColor + '44';
  roundRect(ctx, gaugeX, gaugeY, gaugeW * score, gaugeH, 6); ctx.fill();

  ctx.strokeStyle = scoreColor;
  ctx.lineWidth = 2;
  roundRect(ctx, gaugeX, gaugeY, gaugeW, gaugeH, 6); ctx.stroke();

  // Label
  ctx.fillStyle = scoreColor;
  ctx.font = 'bold 15px "Orbitron", monospace';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(`⚖  ${report.balanceLabel}`, gaugeX + 14, gaugeY + gaugeH / 2 - 7);

  ctx.fillStyle = DIM;
  ctx.font = '10px "Share Tech Mono", monospace';
  ctx.fillText(
    `ratio ${fmt1(report.balanceRatio)}× · target ${5}× · score ${report.balanceScore}/100`,
    gaugeX + 14, gaugeY + gaugeH / 2 + 9,
  );

  ctx.fillStyle = WHITE;
  ctx.font = 'bold 18px "Orbitron", monospace';
  ctx.textAlign = 'right';
  ctx.fillText(`${report.balanceScore}`, gaugeX + gaugeW - 14, gaugeY + gaugeH / 2);

  y1 = gaugeY + gaugeH + 18;
  y2 = y1;

  // ── Column headers ─────────────────────────────────────────────────
  sectionHeader(ctx, 'TIME TO KILL  —  YOU', col1X, y1, colW, '#ff4444');
  y1 += 28;
  sectionHeader(ctx, 'TIME TO KILL  —  ENEMIES', col2X, y2, colW, '#00ff88');
  y2 += 28;

  // Longest TTK = full bar
  const maxTTKPlayer = Math.max(...report.threats.map(e => e.ttk), 1);
  const worstEnemyTTK = Math.max(...report.weaponVsEnemies.map(w2 => w2.ttk), 1);

  // ── TTK-Player bars (left column) ─────────────────────────────────
  for (const threat of report.threats) {
    const pct = Math.min(1, threat.ttk / maxTTKPlayer);
    // Dangerous = short bar (red), safe = long bar (green)
    const barColor = threat.ttk < 5 ? '#ff3333' : threat.ttk < 15 ? '#ffcc00' : '#00ff88';
    hBar(ctx, col1X, y1, colW, 22, pct, barColor,
      `${threat.name}  ·  ${threat.hitsToKill} hits  ·  ${fmt1(threat.ttk)}s`,
      `${fmt1(threat.dpsToPlayer * 60)} dmg/min`,
    );
    y1 += 28;
  }

  // ── TTK-Enemy bars (right column) ─────────────────────────────────
  // One bar per unique enemy, showing best weapon's TTK
  const seenIds: string[] = [];
  for (const v of report.weaponVsEnemies) {
    if (!seenIds.includes(v.enemyId)) seenIds.push(v.enemyId);
  }
  const enemies = seenIds;
  for (const enemyId of enemies) {
    const byEnemy = report.weaponVsEnemies.filter(v => v.enemyId === enemyId);
    const best = byEnemy.reduce((a, b) => a.ttk < b.ttk ? a : b);
    const pct = Math.min(1, best.ttk / worstEnemyTTK);
    const barColor = best.ttk < 2 ? '#00ff88' : best.ttk < 8 ? '#ffcc00' : '#ff3333';
    hBar(ctx, col2X, y2, colW, 22, pct, barColor,
      `${best.enemyName}  ·  ${best.weaponName}`,
      `${fmt1(best.dps)} dps  ·  ${fmt1(best.ttk)}s`,
    );
    y2 += 28;

    // Sub-bars for other weapons (dimmer)
    for (const alt of byEnemy.filter(v => v !== best)) {
      const altPct = Math.min(1, alt.ttk / worstEnemyTTK);
      hBar(ctx, col2X + 16, y2, colW - 16, 18, altPct, '#4488ff',
        `  ↳ ${alt.weaponName}`,
        `${fmt1(alt.ttk)}s`,
      );
      y2 += 24;
    }
  }

  // ── Effective HP summary ───────────────────────────────────────────
  const sumY = Math.max(y1, y2) + 12;
  ctx.fillStyle = DIM;
  ctx.font = '10px "Share Tech Mono", monospace';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillText(
    `Effective HP: ${Math.round(report.playerEffectiveHP)} (HP + Shield)  ·  Avg enemy TTK you: ${fmt1(report.avgPlayerTTK)}s  ·  Avg your TTK enemy: ${fmt1(report.avgEnemyTTK)}s`,
    col1X, sumY,
  );
}

// ─── ITEMS Tab ────────────────────────────────────────────────────────────────

function renderItemsTab(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  w: number,
  startY: number,
  maxH: number,
) {
  const ROW = 20;
  const COL_L = 24;
  const COL_R = w / 2 + 8;
  const COL_W = w / 2 - 32;
  let yL = startY + 8;
  let yR = startY + 8;

  // ── Helpers ──
  function secHead(label: string, x: number, y: number) {
    ctx.font = 'bold 11px "Orbitron", monospace';
    ctx.fillStyle = TEAL;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, x, y + 8);
    ctx.strokeStyle = TEAL + '44';
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(x, y + 17); ctx.lineTo(x + COL_W, y + 17); ctx.stroke();
    return y + 22;
  }
  function row(name: string, sub: string, color: string, x: number, y: number) {
    ctx.font = 'bold 12px "Share Tech Mono", monospace';
    ctx.fillStyle = color;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(name, x + 8, y + ROW / 2);
    if (sub) {
      ctx.font = '10px "Share Tech Mono", monospace';
      ctx.fillStyle = DIM;
      const nameW = ctx.measureText(name).width + 14;
      ctx.fillText(sub, x + 8 + nameW, y + ROW / 2);
    }
    return y + ROW;
  }

  // ── LEFT COLUMN: Weapons + Percs ──
  yL = secHead('WEAPONS', COL_L, yL);
  const ws = getWeaponSystemState();
  const weapons = ws?.weapons ?? [];
  if (weapons.length === 0) {
    yL = row('None equipped', '', DIM, COL_L, yL);
  } else {
    for (const w of weapons) {
      if (yL > startY + maxH - ROW) break;
      const def = getWeaponDef(w.definitionId);
      const name = def?.name ?? w.definitionId;
      const lvl = w.level > 0 ? `Lv${w.level}` : '';
      yL = row(name, lvl, '#ffd700', COL_L, yL);
    }
  }

  yL += 6;
  yL = secHead('PEANUT PERCS', COL_L, yL);
  const percs = getPercSlotDisplayData();
  if (percs.length === 0) {
    yL = row('None active', '', DIM, COL_L, yL);
  } else {
    for (const p of percs) {
      if (yL > startY + maxH - ROW) break;
      const sub = `Lv${p.level}  ${p.totalBonusDisplay}`;
      yL = row(p.percName, sub, p.accentColor || TEAL, COL_L, yL);
    }
  }

  // ── RIGHT COLUMN: Bullshiii Items ──
  yR = secHead('BUNCHA BULLSHIIII', COL_R, yR);
  const itemSt = getItemState();
  const inv = itemSt?.inventory ?? [];
  if (inv.length === 0) {
    yR = row('No items yet', '', DIM, COL_R, yR);
  } else {
    for (const slot of inv) {
      if (yR > startY + maxH - ROW) break;
      const stackStr = slot.stacks > 1 ? `×${slot.stacks}` : '';
      const rarityColor = slot.item.rarity === 'legendary' ? '#ff9900'
        : slot.item.rarity === 'epic' ? '#bf80ff'
        : slot.item.rarity === 'rare' ? '#4a9eff'
        : WHITE;
      yR = row(slot.item.name, stackStr, rarityColor, COL_R, yR);
    }
  }
}

// ─── HISTORY Tab ─────────────────────────────────────────────────────────────

function renderHistoryTab(
  ctx: CanvasRenderingContext2D,
  w: number,
  startY: number,
  _h: number,
) {
  let y = startY + 8;

  sectionHeader(ctx, 'RUN HISTORY  —  LAST 8 RUNS', 24, y, w - 48, TEAL);
  y += 30;

  if (runHistory.length === 0) {
    ctx.fillStyle = DIM;
    ctx.font = '13px "Share Tech Mono", monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('No completed runs yet this session.', w / 2, y + 40);
    return;
  }

  const rowH = 70;
  const colW = (w - 48) / 2;

  for (let i = 0; i < runHistory.length; i++) {
    const snap = runHistory[i];
    const col = i % 2;
    const row = Math.floor(i / 2);
    const rx = 24 + col * (colW + 16);
    const ry = y + row * (rowH + 10);

    // Card background
    ctx.fillStyle = 'rgba(0,255,200,0.04)';
    roundRect(ctx, rx, ry, colW, rowH, 6); ctx.fill();
    ctx.strokeStyle = 'rgba(0,255,200,0.2)';
    ctx.lineWidth = 1;
    roundRect(ctx, rx, ry, colW, rowH, 6); ctx.stroke();

    // Score bar
    const scoreColor = snap.balanceScore < 35 ? '#ff3333' : snap.balanceScore < 65 ? '#ffcc00' : '#00ff88';
    ctx.fillStyle = scoreColor + '33';
    ctx.fillRect(rx + 1, ry + 1, (colW - 2) * (snap.balanceScore / 100), rowH - 2);

    // Text
    ctx.fillStyle = WHITE;
    ctx.font = 'bold 12px "Orbitron", monospace';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(`RUN #${snap.runNum}  ·  Floor ${snap.floor}  ·  ${fmtTime(snap.timeSurvived)}`, rx + 10, ry + 8);

    ctx.fillStyle = DIM;
    ctx.font = '10px "Share Tech Mono", monospace';
    ctx.fillText(`Kills: ${snap.killCount}  ·  Lv ${snap.finalLevel}  ·  Balance: ${snap.balanceScore}/100`, rx + 10, ry + 26);

    ctx.fillStyle = scoreColor;
    ctx.font = 'bold 11px "Share Tech Mono", monospace';
    ctx.fillText(
      `Avg enemy TTK you: ${fmt1(snap.avgPlayerTTK)}s  ·  Your avg TTK: ${fmt1(snap.avgEnemyTTK)}s`,
      rx + 10, ry + 44,
    );
  }
}

// ─── Drawing helpers ─────────────────────────────────────────────────────────

function sectionHeader(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number, y: number, w: number,
  color: string,
) {
  ctx.fillStyle = color + '22';
  ctx.fillRect(x, y, w, 20);
  ctx.fillStyle = color;
  ctx.font = 'bold 9px "Share Tech Mono", monospace';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, x + 6, y + 10);
}

function hBar(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  pct: number, color: string,
  leftLabel: string, rightLabel: string,
) {
  // Background
  ctx.fillStyle = 'rgba(255,255,255,0.05)';
  ctx.fillRect(x, y, w, h);

  // Fill
  ctx.fillStyle = color + '55';
  ctx.fillRect(x, y, w * pct, h);
  ctx.fillStyle = color;
  ctx.fillRect(x, y, Math.min(3, w * pct), h); // left accent stripe

  // Border
  ctx.strokeStyle = 'rgba(255,255,255,0.10)';
  ctx.lineWidth = 1;
  ctx.strokeRect(x, y, w, h);

  // Labels
  ctx.fillStyle = WHITE;
  ctx.font = '10px "Share Tech Mono", monospace';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(leftLabel, x + 8, y + h / 2);

  ctx.fillStyle = DIM;
  ctx.textAlign = 'right';
  ctx.fillText(rightLabel, x + w - 6, y + h / 2);
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}
